import i18n from "../../../language/i18n";
import { SecondaryButton } from "../../../library";
import { outcomeDetailStyles } from "./OutcomeDetailStyles";

/**
 * Opt-in "Konu Anlatımına Git" on weak outcome rows. Passed in as ONE object rather than resolved
 * here so this stays a presentational tree: the owner (ReportCardView) holds the subject-description
 * list, the modal and the navigation, and this component only decides which rows earn the affordance.
 * Omit it and the tree renders exactly as it did — which is what the per-ödev report still wants.
 */
export interface IOutcomeDetailSubjectDescriptionNavigation {
  // Is there a konu anlatımı behind this outcome code at all? A row with no destination must not
  // grow a button that then does nothing.
  canNavigate: (outcomeCode?: string | null) => boolean;
  onNavigate: (target: {
    code?: string | null;
    name?: string | null;
    scorePercent?: number | null;
  }) => void;
  // The barem: rows scoring BELOW this get the affordance. Defaults to the 70 the score pill already
  // uses for its green tier, so "not green" and "worth studying again" are the same line.
  thresholdPercentage?: number;
}

interface OutcomeDetailProps {
  categoryActivityAnswerDTO?: any;
  subjectDescriptionNavigation?: IOutcomeDetailSubjectDescriptionNavigation | null;
}

const DEFAULT_SUBJECT_DESCRIPTION_THRESHOLD = 70;

const OutcomeDetail = (props: OutcomeDetailProps) => {
  const { categoryActivityAnswerDTO, subjectDescriptionNavigation } = props;

  const getScoreColor = (pct: number) => {
    if (pct >= 70) return "qbb-score-high";
    if (pct >= 40) return "qbb-score-mid";
    return "qbb-score-low";
  };

  const getScoreBg = (pct: number) => {
    if (pct >= 70) return "qbb-score-bg-high";
    if (pct >= 40) return "qbb-score-bg-mid";
    return "qbb-score-bg-low";
  };

  const RenderRecursivelyCategoryList = (
    categoryActivityAnswerDTO: any,
    depth: number,
  ): any => {
    const { categoryDTO, subCategoryActivityAnswerDTOList } =
      categoryActivityAnswerDTO;

    const totalCount = categoryActivityAnswerDTO.aggregatedActivityCount ?? 0;
    const percentage =
      categoryActivityAnswerDTO.aggregatedAveragePercentage ?? 0;
    const scoreColor = getScoreColor(percentage);
    const scoreBg = getScoreBg(percentage);
    // Natural code order ("1.1.10" after "1.1.2").
    const sortedSubList = subCategoryActivityAnswerDTOList
      ? [...subCategoryActivityAnswerDTOList].sort((a: any, b: any) =>
          String(a.categoryDTO?.code || "").localeCompare(
            String(b.categoryDTO?.code || ""),
            undefined,
            { numeric: true },
          ),
        )
      : [];

    // A row is either a CATEGORY (it opens further rows below it) or an OUTCOME (an outcome leaf).
    // The split is carried by whether it has children, not by depth: how many levels a curriculum
    // tree has varies by version, but an outcome being a leaf does not.
    const hasSubCategories = sortedSubList.length > 0;

    // "Konu Anlatımına Git", on the same terms the güçlü/zayıf cards offer it:
    //  - LEAVES only. A konu anlatımı is built on an outcome, and the leaf set here is exactly the set
    //    `constructOutcomeActivityScoreList` turns into cards on the other tab — so both tabs offer
    //    the jump on the same rows, never one on a ünite heading the other skips.
    //  - EVIDENCE only. `aggregatedAveragePercentage` is null (→ 0 here) for an outcome with no solved
    //    activity, and a 0% with nothing behind it is not a weakness to send anyone to study.
    //  - BELOW the barem. Default 70%: the pill is already green from 70 up, so the affordance
    //    appears on precisely the rows that are NOT green.
    //  - and only where a konu anlatımı actually exists for the code.
    const subjectDescriptionThreshold =
      subjectDescriptionNavigation?.thresholdPercentage ??
      DEFAULT_SUBJECT_DESCRIPTION_THRESHOLD;
    const canGoToSubjectDescription =
      !!subjectDescriptionNavigation &&
      !hasSubCategories &&
      totalCount > 0 &&
      percentage < subjectDescriptionThreshold &&
      subjectDescriptionNavigation.canNavigate(categoryDTO?.code);
    const goToSubjectDescription = () => {
      subjectDescriptionNavigation?.onNavigate({
        code: categoryDTO?.code,
        name: categoryDTO?.name,
        scorePercent: percentage,
      });
    };

    return (
      <div className="qbb-row-wrapper">
        <div
          className={`qbb-row qbb-depth-${Math.min(depth, 2)} ${
            hasSubCategories ? "qbb-row-branch" : "qbb-row-leaf"
          } ${canGoToSubjectDescription ? "qbb-row-has-cta" : ""}`}
          style={{ paddingLeft: `calc(0.9rem + ${depth} * 1.1rem)` }}
          // The whole row opens the modal too, mirroring the güçlü/zayıf cards (where the CARD is the
          // click target). The button below is the keyboard/AT path and stops propagation of its own
          // click, so the two cannot both fire.
          onClick={
            canGoToSubjectDescription ? goToSubjectDescription : undefined
          }
        >
          <div className="qbb-info">
            <p
              className={`qbb-name ${
                depth === 0
                  ? "qbb-name-root"
                  : hasSubCategories
                    ? "qbb-name-sub"
                    : "qbb-name-outcome"
              }`}
            >
              {categoryDTO.name}
            </p>
            <p className="qbb-meta">
              {totalCount} {i18n.t("activity")}
            </p>
          </div>
          <div className="qbb-row-end">
            <span className={`qbb-pct ${scoreBg} ${scoreColor}`}>
              {percentage.toFixed(0)}%
            </span>
            {canGoToSubjectDescription ? (
              <div className="qbb-cta">
                <SecondaryButton
                  title={i18n.t("go_to_subject_description")}
                  size="small"
                  onClick={goToSubjectDescription}
                />
              </div>
            ) : null}
          </div>
        </div>
        {sortedSubList.map((subDTO: any, index: number) => (
          <div key={index}>
            {RenderRecursivelyCategoryList(subDTO, depth + 1)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="qbb-root">
      <style>{outcomeDetailStyles}</style>
      {categoryActivityAnswerDTO
        ? RenderRecursivelyCategoryList(categoryActivityAnswerDTO, 0)
        : null}
    </div>
  );
};

export default OutcomeDetail;
